<script lang="ts">
import AppSelect from '@/components/v2/AppSelect.vue';

export default {
  name: 'DocumentUpload',
  components: {
    AppSelect,
  },
};

interface Document {
  id: number;
  name: string;
  type: DocumentType;
  accountId: number;
  programId?: number;
  policyId?: number;
  policyNumber?: string;
  file: File;
  selected: boolean;
  readyForUpload: boolean;
  tags?: string[];
}

interface ProgramOption extends ProgramDetails {
  selected: boolean;
}

interface Options {
  documentTypes: DocumentType[];
  currentPrograms: ProgramOption[];
  currentProgramsLoaded: boolean;
  currentProgramsLoading: boolean;
  expiredPrograms: ProgramOption[];
  expiredProgramsLoaded: boolean;
  expiredProgramsLoading: boolean;
}

interface SelectedOptions {
  documentType?: DocumentType;
  programId?: number;
  policyId?: number;
  policyNumber?: string;
}
</script>

<script setup lang="ts">
import { ref, reactive, watch, computed } from 'vue';
import {
  CloudUpload,
  Trash2,
  ChevronsRight,
  ChevronsLeft,
  OctagonAlert,
  CircleCheck,
  CircleX,
  X,
} from '@lucide/vue';
import { DocumentDomainTypeClientUploaded, DocumentType } from '@/types/v2/Document';
import { documentService } from '@/services/v2/DocumentService';
import { accountService, policySourceService } from '@/services/api';
import { ProgramDetails } from '@/types/Programs';
import { Account } from '@/types/Account';
import { EpicPolicy } from '@/types/tower/Domain';
import { useRouter } from 'vue-router';
import { IsUserAClient } from '@/utilities/AuthUtils.js';
import Store from '@/store/index.js';
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from 'reka-ui';
import Combobox, { type ComboboxOption } from '@/components/v2/Combobox.vue';
import { escapeHtml, sanitizeHighlight } from '@/utilities/Sanitization';
import { externalPolicyName } from '@/utilities/OrgConfig';
import { chipColors } from '@/utilities/Color';

let nextDocumentId = 0;

const props = defineProps<{
  account: Account;
}>();

const router = useRouter();

// -----
// refs
// -----

const documentsSectionRef = ref<HTMLElement | null>(null);
const documentTypesSectionRef = ref<HTMLElement | null>(null);
const programsAndPoliciesSectionRef = ref<HTMLElement | null>(null);
const tagsSectionRef = ref<HTMLElement | null>(null);

const documents = ref([] as Partial<Document>[]);
const selectedOptions = reactive<SelectedOptions>({});
const showCurrentProgramsOnly = ref(true);
const options = reactive<Options>({
  documentTypes: [],
  currentPrograms: [],
  currentProgramsLoaded: false,
  currentProgramsLoading: false,
  expiredPrograms: [],
  expiredProgramsLoaded: false,
  expiredProgramsLoading: false,
});

const draggingOver = ref(false);
const fileInput = ref<HTMLInputElement | null>(null);
const allAccountPolicies = ref<EpicPolicy[]>([]);
const allAccountPoliciesLoading = ref(false);
const externalPolicySourceIDs = ref<number[]>([]);
const currentProgramPoliciesTab = ref<string>('programs');
const showCurrentPoliciesOnly = ref(true);
const policySearchQuery = ref('');
const uploading = ref<boolean>(false);
const availableTags = ref<ComboboxOption[]>([]);
const selectedTags = ref<ComboboxOption[]>([]);
const selectedTag = ref<string>('');
const isUpdatingTags = ref<boolean>(false);

// ---------
// computed
// ---------

const selectedDocuments = computed(() => {
  return documents.value.filter((document) => document.selected);
});

const allDocumentsSelected = computed(() => {
  return documents.value.length > 0 && documents.value.every((document) => document.selected);
});

const accountDocumentTypes = computed(() => {
  return options.documentTypes
    .filter((documentType) => documentType.domainName === 'Account')
    .sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
});

const policyDocumentTypes = computed(() => {
  return options.documentTypes
    .filter((documentType) => documentType.domainName === 'Policy')
    .sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
});

const programs = computed(() => {
  if (showCurrentProgramsOnly.value) {
    return options.currentPrograms;
  } else {
    return options.expiredPrograms;
  }
});

const programsLoaded = computed(() => {
  return showCurrentProgramsOnly.value ? options.currentProgramsLoaded : options.expiredProgramsLoaded;
});

const policiesFromSelectedProgram = computed(() => {
  if (!selectedOptions.programId) return [];

  if (showCurrentProgramsOnly.value) {
    const program = options.currentPrograms.find((program) => program.program.id === selectedOptions.programId);
    return program?.policies ?? [];
  } else {
    const program = options.expiredPrograms.find((program) => program.program.id === selectedOptions.programId);
    return program?.policies ?? [];
  }
});

const readyForUpload = computed(() => {
  return documents.value.every((document) => document.readyForUpload);
});

const isClientRole = computed(() => {
  return IsUserAClient(Store.state.user);
});

const clientUploadedDocumentTypes = computed(() => {
  return options.documentTypes
    .filter((documentType) => documentType.domainName === DocumentDomainTypeClientUploaded)
    .sort((a, b) => a.name.localeCompare(b.name));
});

const filteredAccountPolicies = computed(() => {
  if (!policySearchQuery.value.trim()) {
    return allAccountPolicies.value;
  }

  const searchTerm = policySearchQuery.value.toLowerCase().trim();
  return allAccountPolicies.value.filter((policy) => {
    return (
      policy.description?.toLowerCase().includes(searchTerm) ||
      policy.policyID?.toLowerCase().includes(searchTerm) ||
      policy.carrierGroup?.toLowerCase().includes(searchTerm) ||
      policy.coverageType?.toLowerCase().includes(searchTerm)
    );
  });
});

const availableTagsFiltered = computed(() => {
  return availableTags.value.filter(tag => 
    !selectedTags.value.some(selectedTag => selectedTag.value === tag.value)
  );
});

const tagsComboboxKey = computed(() => {
  // Create a unique key based on selected document indices to force Combobox remount
  return documents.value
    .map((doc, index) => doc.selected ? index : null)
    .filter(idx => idx !== null)
    .join('-') || 'none';
});

// ---------
// watchers
// ---------

watch(showCurrentProgramsOnly, async () => {
  selectedOptions.programId = undefined;
  selectedOptions.policyId = undefined;
  selectedOptions.policyNumber = undefined;
  await loadPrograms();
});

watch(currentProgramPoliciesTab, async (newTab) => {
  if (newTab === 'policies') {
    await loadAllAccountPolicies();
  }
});

watch(showCurrentPoliciesOnly, async () => {
  if (currentProgramPoliciesTab.value === 'policies') {
    await loadAllAccountPolicies();
  }
});

watch(documents, async (newDocuments) => {
  if (newDocuments.length > 0) {
    addEventListener('click', handleClickOutside);
    addEventListener('mouseup', handleGlobalMouseUp);

    if (isClientRole.value) {
      await loadDocumentTypes();

      // client document uploads only have 1 type and is automatically assigned

      documents.value.forEach((document) => {
        document.type = undefined;
      });

      validateDocuments();
    } else {
      await Promise.all([loadDocumentTypes(), loadPrograms()]);
    }

    // Load tags if not already loaded
    if (availableTags.value.length === 0) {
      await getTags();
    }
  } else {
    removeEventListener('click', handleClickOutside);
    removeEventListener('mouseup', handleGlobalMouseUp);
  }
});

watch(selectedDocuments, (newSelectedDocuments) => {
  // Always clear the input when selection changes
  selectedTag.value = '';

  // no documents are selected
  if (newSelectedDocuments.length === 0) {
    selectedOptions.documentType = undefined;
    selectedOptions.policyId = undefined;
    selectedOptions.programId = undefined;
    selectedTags.value = [];
  }

  // only one document is selected
  else if (newSelectedDocuments.length === 1) {
    selectedOptions.documentType = newSelectedDocuments[0]?.type;
    selectedOptions.policyId = newSelectedDocuments[0]?.policyId;
    selectedOptions.programId = newSelectedDocuments[0]?.programId;
    // Load tags from the selected document
    selectedTags.value = (newSelectedDocuments[0]?.tags || []).map(tag => ({
      label: tag,
      value: tag
    }));
  }

  // multiple documents are selected
  else if (newSelectedDocuments.length > 1) {
    // if they all have the same document type, set the selected document type
    if (
      newSelectedDocuments[0]?.type &&
      newSelectedDocuments.reduce((acc, document) => acc && document?.type === newSelectedDocuments[0]?.type, true)
    ) {
      selectedOptions.documentType = newSelectedDocuments[0].type;
    } else {
      selectedOptions.documentType = undefined;
    }

    // if they all have the same policy, set the selected policy
    if (
      newSelectedDocuments[0]?.policyId &&
      newSelectedDocuments.reduce(
        (acc, document) => acc && document?.policyId === newSelectedDocuments[0]?.policyId,
        true,
      )
    ) {
      selectedOptions.policyId = newSelectedDocuments[0].policyId;
      selectedOptions.programId = newSelectedDocuments[0].programId;
    }

    // if they have different document types, clear the selected options
    else {
      selectedOptions.policyId = undefined;
      selectedOptions.programId = undefined;
    }

    // Get intersection of tags from all selected documents
    const firstDocTags = newSelectedDocuments[0]?.tags || [];
    const commonTags = firstDocTags.filter(tag => 
      newSelectedDocuments.every(doc => doc.tags?.includes(tag))
    );
    selectedTags.value = commonTags.map(tag => ({
      label: tag,
      value: tag
    }));
  }
});

// ---------------------
// functions & handlers
// ---------------------

function handleFileDragOver(e: DragEvent) {
  e.preventDefault();
  e.stopPropagation();
  draggingOver.value = true;
}

function handleFileDrop(e: DragEvent) {
  e.preventDefault();
  e.stopPropagation();
  draggingOver.value = false;

  if (e.dataTransfer?.items) {
    // use the DataTransferItemList interface if available
    let droppedDocuments: Partial<Document>[] = [];
    [...e.dataTransfer.items].forEach((item) => {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          droppedDocuments.push({
            id: nextDocumentId++,
            name: file.name,
            file: file,
            tags: [],
          });
        }
      }
    });
    documents.value = [...documents.value, ...droppedDocuments];
  } else if (e.dataTransfer?.files) {
    // otherwise use the DataTransfer interface
    let droppedDocuments: Partial<Document>[] = [];
    [...e.dataTransfer.files].forEach((file) => {
      droppedDocuments.push({
        id: nextDocumentId++,
        name: file.name,
        file: file,
        tags: [],
      });
    });
    documents.value = [...documents.value, ...droppedDocuments];
  }

  // select the document if only one was dropped
  if (documents.value.length === 1) {
    documents.value[0].selected = true;
  }
}

function handleFileDragLeave() {
  draggingOver.value = false;
}

function handleSelectFiles() {
  if (fileInput.value) {
    fileInput.value.click();
  }
}

function handleFilesSelected(e: Event) {
  const target = e.target as HTMLInputElement;
  if (target.files) {
    const selectedDocuments = [...target.files].map((file) => ({
      id: nextDocumentId++,
      name: file.name,
      file: file,
      tags: [],
    }));
    documents.value = [...documents.value, ...selectedDocuments];

    // select the document if only one was selected
    if (documents.value.length === 1) {
      documents.value[0].selected = true;
    }
  }

  e.preventDefault();
  e.stopPropagation();
}

function handleDeleteDocument(documentIndex: number) {
  documents.value = documents.value.filter((_, index) => index !== documentIndex);
}

function handleToggleSelectAll() {
  const shouldSelect = !allDocumentsSelected.value;
  documents.value = documents.value.map((document) => ({ ...document, selected: shouldSelect }));
}

let dragStartIndex: number | null = null;
const dragSelecting = ref(false);

function handleDocumentMouseDown(index: number, event: MouseEvent) {
  // Suppress browser text-selection so dragging over rows doesn't highlight their labels.
  event.preventDefault();
  dragStartIndex = index;
  dragSelecting.value = false;
}

function handleDocumentMouseEnter(index: number) {
  if (dragStartIndex === null) return;
  if (!dragSelecting.value) {
    // Mouse has moved off the mousedown row for the first time — commit to a drag
    // and mark the starting row selected (its own mouseenter never fires).
    const startDoc = documents.value[dragStartIndex];
    if (startDoc) startDoc.selected = true;
    dragSelecting.value = true;
  }
  const doc = documents.value[index];
  if (doc) doc.selected = true;
}

function handleDocumentClick(index: number) {
  if (dragSelecting.value) {
    // Drag just ended on this row — swallow the click so we don't toggle it back off.
    dragSelecting.value = false;
    dragStartIndex = null;
    return;
  }
  const doc = documents.value[index];
  if (doc) doc.selected = !doc.selected;
  dragStartIndex = null;
}

function handleGlobalMouseUp() {
  // Defer reset so any click event (which fires after mouseup) still sees dragSelecting.
  // Guards against mouseup happening outside any row, which would otherwise leave stale
  // state and make later hovers trigger selection.
  requestAnimationFrame(() => {
    dragStartIndex = null;
    dragSelecting.value = false;
  });
}

function handleSelectDocumentType(documentType: DocumentType) {
  if (selectedOptions.documentType === documentType) {
    selectedOptions.documentType = undefined;
  } else {
    selectedOptions.documentType = documentType;
  }

  selectedDocuments.value.map((document) => {
    document.type = selectedOptions.documentType;
  });

  validateDocuments();
}

function handleSelectProgram(programId: number) {
  if (selectedOptions.programId === programId) {
    selectedOptions.programId = undefined;
  } else {
    selectedOptions.programId = programId;
  }
}

function handleSelectPolicy(policy: EpicPolicy) {
  if (!policy || !policy.uniqPolicy) return;

  if (selectedOptions.policyId === Number(policy.uniqPolicy)) {
    selectedOptions.policyId = undefined;
    selectedOptions.policyNumber = undefined;
  } else {
    selectedOptions.policyId = Number(policy.uniqPolicy);
    selectedOptions.policyNumber = policy.policyID;
  }

  selectedDocuments.value.map((document) => {
    document.policyId = selectedOptions.policyId;
    document.policyNumber = selectedOptions.policyNumber;
    document.programId = selectedOptions.programId;
  });

  validateDocuments();
}

function validateDocuments() {
  documents.value.forEach((document) => {
    if (!document.type) {
      document.readyForUpload = false;
    } else if (isPolicyDocumentType(document.type) && !document.policyId) {
      document.readyForUpload = false;
    } else {
      document.readyForUpload = true;
    }
  });
}

function handleClickOutside(event: MouseEvent) {
  const target = event.target as HTMLElement;
  
  // Check if click is inside a combobox dropdown (which is rendered in a portal)
  const isInsideComboboxDropdown = target.closest('[role="listbox"]') !== null;
  
  if (
    documentsSectionRef.value &&
    !documentsSectionRef.value.contains(event.target as Node) &&
    documentTypesSectionRef.value &&
    !documentTypesSectionRef.value.contains(event.target as Node) &&
    programsAndPoliciesSectionRef.value &&
    !programsAndPoliciesSectionRef.value.contains(event.target as Node) &&
    tagsSectionRef.value &&
    !tagsSectionRef.value.contains(event.target as Node) &&
    !isInsideComboboxDropdown
  ) {
    documents.value = documents.value.map((document) => ({ ...document, selected: false }));
  }
}

function handleCancel() {
  router.back();
}

async function handleUpload() {
  if (!readyForUpload.value) return;

  uploading.value = true;

  const requests = documents.value.map((document) => {
    return documentService.uploadDocumentForPolicyOrAccount({
      domain: document.type!.domainName,
      type: document.type!.name,
      displayName: document.name,
      fileName: document.name!,
      accountId: Number(props.account.accountID),
      policyId: document.policyId,
      document: document.file as File,
      tags: document.tags && document.tags.length > 0 ? document.tags : undefined,
    });
  });

  try {
    await Promise.all(requests);
    await router.replace(`/account/${props.account.accountID}/documents`);
  } catch (e) {
    console.error(e);
  }

  uploading.value = false;
}

function handleRemoveDocumentType(document: Partial<Document>) {
  selectedOptions.documentType = undefined;
  document.type = undefined;
  validateDocuments();
}

function handleRemovePolicy(document: Partial<Document>) {
  selectedOptions.policyId = undefined;
  selectedOptions.programId = undefined;
  selectedOptions.policyNumber = undefined;
  document.programId = undefined;
  document.policyId = undefined;
  document.policyNumber = undefined;
  validateDocuments();
}

function isPolicyDocumentType(documentType: DocumentType): boolean {
  return policyDocumentTypes.value.some((type) => type === documentType);
}

function highlightText(text: string | undefined, searchQuery: string): string {
  if (!text || !searchQuery.trim()) {
    return escapeHtml(text || '');
  }

  // Escape the text to prevent XSS
  const escapedText = escapeHtml(text);
  const searchTerm = searchQuery.trim();
  const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const highlighted = escapedText.replace(regex, '<mark class="bg-yellow-200 px-0.5 rounded">$1</mark>');
  
  return sanitizeHighlight(highlighted);
}

async function loadDocumentTypes(): Promise<void> {
  if (options.documentTypes.length === 0) {
    options.documentTypes = await documentService.getDocumentTypes();
  }
}

async function loadPrograms(): Promise<void> {
  if (showCurrentProgramsOnly.value && !options.currentProgramsLoaded && !options.currentProgramsLoading) {
    options.currentProgramsLoading = true;
    const programs = await getProgramsForAccount(Number(props.account.accountID), false);
    options.currentPrograms = await loadProgramDetails(Number(props.account.accountID), programs);
    options.currentProgramsLoaded = true;
    options.currentProgramsLoading = false;
  } else if (!showCurrentProgramsOnly.value && !options.expiredProgramsLoaded && !options.expiredProgramsLoading) {
    options.expiredProgramsLoading = true;
    const programs = await getProgramsForAccount(Number(props.account.accountID), true);
    options.expiredPrograms = await loadProgramDetails(Number(props.account.accountID), programs);
    options.expiredProgramsLoaded = true;
    options.expiredProgramsLoading = false;
  }

  async function getProgramsForAccount(accountID: number, showExpired: boolean): Promise<ProgramOption[]> {
    return (await accountService.getProgramsForAccount(accountID, showExpired)).map((prog) => ({
      program: prog,
      policies: [],
      selected: false,
    }));
  }

  async function loadProgramDetails(accountID: number, programs: ProgramOption[]): Promise<ProgramOption[]> {
    const programDetails = await Promise.all(
      programs.map(async (program) => {
        return accountService.getProgramDetails(accountID, program.program.id, isClientRole.value);
      }),
    );

    programDetails.forEach((programDetail, index) => {
      programs[index].policies = programDetail.policies;
    });

    return programs;
  }
}

async function loadAllAccountPolicies(): Promise<void> {
  allAccountPoliciesLoading.value = true;
  try {
    await loadExternalPolicySourceIDs();
    allAccountPolicies.value = await accountService.policiesById(
      Number(props.account.accountID),
      !showCurrentPoliciesOnly.value, // include expired
      false, // withoutProgram
      true, // showExternal
      isClientRole.value,
    );
  } catch (error) {
    console.error('Failed to load account policies:', error);
    allAccountPolicies.value = [];
  } finally {
    allAccountPoliciesLoading.value = false;
  }
}

async function loadExternalPolicySourceIDs(): Promise<void> {
  if (externalPolicySourceIDs.value.length > 0) {
    return;
  }
  const sources = await policySourceService.list();
  externalPolicySourceIDs.value = sources.filter((ps) => ps.IsExternal).map((ps) => ps.ID);
}

function isExternalPolicy(policy: EpicPolicy): boolean {
  return policy.policySourceID !== undefined && externalPolicySourceIDs.value.includes(policy.policySourceID);
}

const externalChipLabel = externalPolicyName();
const externalChipColor = `${chipColors[0].bgColor} ${chipColors[0].textColor}`;

async function getTags() {
  try {
    const tags = await documentService.getDocumentTags();
    availableTags.value = (tags || []).map(tag => ({
      label: tag.name,
      value: tag.name
    }));
  } catch (error) {
    console.error('Failed to load tags:', error);
    availableTags.value = [];
  }
}

function handleTagSelect(tag: ComboboxOption) {
  isUpdatingTags.value = true;
  
  // Apply tag to all selected documents
  documents.value.forEach((document) => {
    if (document.selected) {
      if (!document.tags) {
        document.tags = [];
      }
      if (!document.tags.includes(tag.value)) {
        document.tags.push(tag.value);
      }
    }
  });
  
  // Update selectedTags to reflect the new state
  if (!selectedTags.value.some(selectedTag => selectedTag.value === tag.value)) {
    selectedTags.value = [...selectedTags.value, tag];
  }
  
  // Clear the selection after adding
  selectedTag.value = '';
  
  isUpdatingTags.value = false;
}

function handleTagRemove(tag: ComboboxOption) {
  isUpdatingTags.value = true;
  
  // Remove tag from all selected documents
  documents.value.forEach((document) => {
    if (document.selected && document.tags) {
      document.tags = document.tags.filter(t => t !== tag.value);
    }
  });
  
  // Update selectedTags to reflect the new state
  selectedTags.value = selectedTags.value.filter(selectedTag => selectedTag.value !== tag.value);
  
  isUpdatingTags.value = false;
}
</script>

<template>
  <div class="h-full">
    <div
      v-if="documents.length == 0"
      class="flex h-full w-full flex-col items-center gap-4 px-5 py-6"
      @dragover="handleFileDragOver"
      @drop="handleFileDrop"
      @dragleave="handleFileDragLeave">
      <a
        class="btn-primary/80 btn flex gap-1 self-start border-none p-1 px-2 text-gray-100 btn-sm"
        @click="handleCancel">
        <ChevronsLeft :size="20" />
        <span>Go Back</span>
      </a>
      <div
        class="h-96 w-full max-w-7xl rounded-lg border-2 border-dashed border-gray-400 transition-all"
        :class="{ 'bg-blue-100': draggingOver }">
        <div class="flex h-full flex-col items-center justify-center">
          <CloudUpload :size="55" class="text-gray-400" />
          <div class="text-2xl font-bold text-gray-400">Drag and drop files here</div>
          <div class="text-xl text-gray-400">or</div>
          <input ref="fileInput" type="file" multiple class="hidden" @change="handleFilesSelected" />
          <button class="btn mt-2 btn-secondary" @click="handleSelectFiles">Select Files</button>
        </div>
      </div>
    </div>

    <div v-else-if="isClientRole" class="mx-8 my-8 flex flex-col gap-4">
      <div>
        <!-- drag and drop -->
        <div
          class="mx-auto w-96 p-4"
          @dragover="handleFileDragOver"
          @drop="handleFileDrop"
          @dragleave="handleFileDragLeave">
          <div
            class="rounded-lg border-2 border-dashed border-gray-500 p-5 transition-all"
            :class="{ 'bg-blue-100': draggingOver }">
            <div class="flex h-full flex-col items-center justify-center">
              <CloudUpload class="text-gray-500" />
              <div class="text-md font-bold text-gray-500">Drag and drop files here</div>
              <div class="text-gray-500">or</div>
              <input ref="fileInput" type="file" multiple class="hidden" @change="handleFilesSelected" />

              <button class="btn btn-sm btn-secondary" @click="handleSelectFiles">Select Files</button>
            </div>
          </div>
        </div>

        <!-- documents -->
        <div class="mx-auto flex max-w-160 flex-col gap-2 p-4" ref="documentsSectionRef">
          <div class="flex flex-col gap-2">
            <div v-for="(document, index) in documents" :key="document.id" data-testid="documents">
              <div
                class="flex items-center justify-between gap-2 rounded-lg border border-gray-200 p-4 shadow-md transition-all hover:bg-gray-50"
                data-testid="document">
                <CircleCheck v-if="!document.readyForUpload" :size="20" class="text-gray-300" />
                <!-- wait for watcher to run -->
                <CircleCheck v-else :size="20" class="text-green-500" />

                <div class="flex-1 overflow-hidden text-base text-ellipsis text-gray-600 italic">
                  {{ document.name }}
                </div>

                <!-- client uploaded document types -->
                <AppSelect
                  v-model="document.type"
                  :options="
                    clientUploadedDocumentTypes.map((type) => ({
                      label: type.name,
                      value: type,
                    })).sort((a, b) => a.label.localeCompare(b.label))
                  "
                  class="w-full"
                  placeholder="Select document type"
                  @update:modelValue="validateDocuments" />

                <div
                  class="group ml-5 flex h-6 w-6 cursor-pointer items-center justify-center rounded-lg border border-solid border-gray-400 transition-all hover:border-red-300"
                  @click="handleDeleteDocument(index)">
                  <Trash2 :size="16" class="text-gray-400 transition-all group-hover:stroke-red-500" />
                </div>
              </div>
            </div>
          </div>

          <!-- form actions -->
          <div class="col-span-2 col-start-1 row-start-1 flex justify-end pt-3">
            <div class="flex justify-end gap-2 rounded-lg">
              <button class="btn bg-base-300 shadow-md btn-sm hover:shadow-none" @click="handleCancel">
                Cancel
              </button>
              <button
                data-testid="document-upload-submit"
                class="btn shadow-md btn-sm hover:shadow-none"
                :class="{ 'btn-primary': readyForUpload, 'btn-disabled': !readyForUpload || uploading }"
                @click="handleUpload">
                <span :class="{'loading loading-dots': uploading}">Upload</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <div class="grid-rows-[repeat(3, auto)] grid h-full grid-cols-2">
        <!-- form actions -->
        <div class="col-span-2 col-start-1 row-start-1 flex justify-end pt-3 pr-4">
          <div class="flex justify-end gap-2 rounded-lg bg-gray-100/75 p-3">
            <button class="btn bg-base-300 shadow-md btn-sm hover:shadow-none" @click="handleCancel">
              Cancel
            </button>
            <button
              data-testid="document-upload-submit"
              class="btn shadow-md btn-sm hover:shadow-none"
              :class="{ 'btn-primary': readyForUpload, 'btn-disabled': !readyForUpload }"
              @click="handleUpload">
              Upload
            </button>
          </div>
        </div>

        <div class="col-start-1 row-span-3 row-start-2">
          <!-- drag and drop -->
          <div
            class="p-4 pt-0"
            @dragover="handleFileDragOver"
            @drop="handleFileDrop"
            @dragleave="handleFileDragLeave">
            <div
              class="rounded-lg border-2 border-dashed border-gray-400 p-5 transition-all"
              :class="{ 'bg-blue-100': draggingOver }">
              <div class="flex h-full flex-col items-center justify-center">
                <CloudUpload class="text-gray-400" />
                <div class="text-md font-bold text-gray-400">Drag and drop files here</div>
                <div class="text-gray-400">or</div>
                <input ref="fileInput" type="file" multiple class="hidden" @change="handleFilesSelected" />
                <button class="btn btn-xs btn-secondary" @click="handleSelectFiles">Select Files</button>
              </div>
            </div>
          </div>

          <!-- documents -->
          <div class="flex flex-col gap-2 p-4" ref="documentsSectionRef">
            <div v-if="documents.length > 0" class="flex justify-end">
              <button
                type="button"
                class="link link-primary text-xs font-semibold"
                data-testid="document-upload-select-all"
                @click.stop="handleToggleSelectAll">
                {{ allDocumentsSelected ? 'Clear All' : 'Select All' }}
              </button>
            </div>
            <div class="flex flex-col gap-2" :class="{ 'select-none': dragSelecting }">
              <div v-for="(document, index) in documents" :key="document.id" data-testid="documents">
                <div
                  class="flex cursor-pointer items-center justify-between gap-2 rounded-lg border border-gray-200 p-4 shadow-md transition-all"
                  :class="{
                    'border-secondary bg-blue-50 shadow-none hover:bg-blue-50': document.selected,
                    'hover:bg-gray-50': !document.selected,
                  }"
                  @mousedown="handleDocumentMouseDown(index, $event)"
                  @mouseenter="handleDocumentMouseEnter(index)"
                  @click="handleDocumentClick(index)"
                  data-testid="document">
                  <OctagonAlert v-if="!document.readyForUpload" :size="20" class="text-red-500" />
                  <CircleCheck v-else :size="20" class="text-green-500" />
                  <div class="flex-1 overflow-hidden text-base text-ellipsis text-gray-600 italic">
                    {{ document.name }}
                  </div>
                  <div class="flex flex-col items-end gap-2">
                    <div v-if="document.type" class="relative badge bg-secondary/30 p-3 badge-md">
                      {{ document.type.name }}
                      <CircleX
                        :size="13"
                        class="absolute -top-1 -right-1 ml-1 cursor-pointer text-red-500"
                        @click.stop="handleRemoveDocumentType(document)" />
                    </div>
                    <div v-else class="text-[11px] text-red-500">Select a document type...</div>
                    <div
                      v-if="document.policyId && document.policyId > 0"
                      class="relative badge flex bg-cyan-100 p-3 badge-md">
                      {{ document.policyNumber }}
                      <CircleX
                        :size="13"
                        class="absolute -top-1 -right-1 ml-1 cursor-pointer text-red-500"
                        @click.stop="handleRemovePolicy(document)" />
                    </div>
                    <div
                      v-else-if="document.type && isPolicyDocumentType(document.type ?? '')"
                      class="text-[11px] text-red-500">
                      Select a policy...
                    </div>
                  </div>
                  <div
                    class="group ml-5 flex h-6 w-6 cursor-pointer items-center justify-center rounded-lg border border-solid border-gray-400 transition-all hover:border-red-300"
                    @click.stop="handleDeleteDocument(index)">
                    <Trash2 :size="16" class="text-gray-400 transition-all group-hover:stroke-red-500" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-start-2 row-span-2 row-start-2">
          <!-- document types -->
          <div class="p-4 pt-0" ref="documentTypesSectionRef">
            <div class="pl-2 text-base text-gray-700">Document Types</div>
            <div
              class="relative flex w-full flex-col gap-6 rounded-lg border border-solid border-gray-200 bg-secondary/10 p-4 shadow-md">
              <div
                v-if="selectedDocuments.length === 0"
                class="absolute h-full w-full cursor-not-allowed rounded-lg bg-gray-100/30 transition-all"></div>
              <div class="flex flex-col gap-2">
                <div class="text-sm font-semibold text-gray-500">Account Document Types</div>
                <div class="flex flex-wrap gap-2 pl-3">
                  <div
                    v-for="documentType in accountDocumentTypes"
                    :key="documentType.id"
                    class="w-fit-content cursor-pointer rounded-lg border border-solid border-gray-300 p-2 shadow-xs transition-all"
                    :class="{
                      'bg-secondary/30': documentType === selectedOptions.documentType,
                      'hover:bg-gray-50': documentType !== selectedOptions.documentType,
                    }"
                    @click="handleSelectDocumentType(documentType)"
                    data-testid="account-document-type">
                    <div class="text-gray-600">{{ documentType.name }}</div>
                  </div>
                </div>
              </div>
              <div class="flex flex-col gap-2">
                <div class="text-sm font-semibold text-gray-500">Policy Document Types</div>
                <div class="flex flex-wrap gap-2 pl-3">
                  <div
                    v-for="documentType in policyDocumentTypes"
                    :key="documentType.id"
                    class="w-fit-content cursor-pointer rounded-lg border border-solid border-gray-300 p-2 shadow-xs transition-all"
                    :class="{
                      'bg-secondary/30': documentType === selectedOptions.documentType,
                      'hover:bg-gray-50': documentType !== selectedOptions.documentType,
                    }"
                    @click="handleSelectDocumentType(documentType)">
                    <div class="text-gray-600">{{ documentType.name }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- programs & policies -->
          <div class="p-4" ref="programsAndPoliciesSectionRef">
            <!-- Tabs -->
            <TabsRoot v-model="currentProgramPoliciesTab" class="w-full">
              <TabsList
                class="inline-flex h-10 items-center justify-center rounded-md bg-slate-100 p-1 text-slate-600">
                <TabsTrigger
                  value="programs"
                  class="inline-flex items-center justify-center rounded-sm px-3 py-1.5 text-sm font-medium whitespace-nowrap ring-offset-white transition-all focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-white data-[state=active]:text-slate-950 data-[state=active]:shadow-sm"
                  >Program & Policies</TabsTrigger
                >
                <TabsTrigger
                  value="policies"
                  class="inline-flex items-center justify-center rounded-sm px-3 py-1.5 text-sm font-medium whitespace-nowrap ring-offset-white transition-all focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-white data-[state=active]:text-slate-950 data-[state=active]:shadow-sm"
                  >Policies</TabsTrigger
                >
              </TabsList>

              <TabsContent value="programs">
                <div
                  class="flex w-full flex-col justify-between rounded-lg border border-solid border-gray-200 bg-cyan-50/50 p-4 shadow-md">
                  <div
                    v-if="selectedDocuments.length === 0"
                    class="h-full w-full cursor-not-allowed rounded-lg bg-gray-100/30 transition-all"></div>

                  <!-- Show Current Only toggle -->
                  <label class="label w-fit gap-1">
                    <span class="label-text text-[11px] text-gray-600">Show Current Only</span>
                    <input
                      type="checkbox"
                      :checked="showCurrentProgramsOnly"
                      v-model="showCurrentProgramsOnly"
                      class="toggle opacity-80 toggle-primary toggle-sm" />
                  </label>

                  <!-- programs -->
                  <div class="flex">
                    <div class="flex w-1/2 flex-col gap-1 p-1">
                      <div v-if="!programsLoaded" class="h-96 w-full skeleton"></div>
                      <div
                        v-else
                        v-for="program in programs"
                        :key="program.program.id"
                        class="flex cursor-pointer flex-col justify-between rounded-lg border border-solid border-gray-200 p-2 shadow-md"
                        :class="{
                          'border-blue-100 bg-blue-50 shadow-none':
                            program.program.id === selectedOptions.programId,
                          'hover:bg-gray-50': program.program.id !== selectedOptions.programId,
                        }"
                        @click="handleSelectProgram(program.program.id)"
                        data-testid="program">
                        <div class="text-gray-600">{{ program.program.name }}</div>
                        <div class="flex flex-wrap justify-between">
                          <div class="text-[11px] text-gray-600">{{ program.program.type }}</div>
                          <div class="flex flex-col items-end gap-1">
                            <div
                              v-for="coverageType in program.program.coverageTypes"
                              :key="coverageType"
                              class="w-fit-content rounded-box bg-[#4E738A] px-1 text-[11px] font-semibold text-gray-100">
                              {{ coverageType }}
                            </div>
                          </div>
                        </div>
                        <div>
                          <div class="text-[11px] text-gray-600">{{ program.policies.length }} Policies</div>
                        </div>
                      </div>
                    </div>
                    <div class="divider m-0 divider-horizontal p-1">
                      <ChevronsRight :size="28" />
                    </div>
                    <!-- policies -->
                    <div class="flex w-1/2 flex-col gap-1 p-1">
                      <div
                        v-for="policy in policiesFromSelectedProgram"
                        :key="policy.policyID"
                        class="flex w-full cursor-pointer flex-col items-center justify-between rounded-lg border border-solid border-gray-200 p-2 shadow-md"
                        :class="{
                          'border-blue-100 bg-blue-50 shadow-none':
                            Number(policy.uniqPolicy) === selectedOptions.policyId,
                          'hover:bg-gray-50': Number(policy.uniqPolicy) !== selectedOptions.policyId,
                        }"
                        @click="handleSelectPolicy(policy)"
                        data-testid="policy">
                        <div class="w-full text-gray-600">{{ policy.description }}</div>
                        <div class="flex w-full flex-col justify-between">
                          <div class="text-[11px] text-gray-600">Carrier: {{ policy.carrierGroup }}</div>
                          <div class="text-[11px] text-gray-600">Coverage Type: {{ policy.coverageType }}</div>
                          <div class="text-[11px] text-gray-600">Policy Number: {{ policy.policyID }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="policies">
                <div
                  class="relative rounded-lg border border-solid border-gray-200 bg-cyan-50/50 p-4 shadow-md">
                  <!-- Controls row with toggle and search -->
                  <div class="mb-3 flex items-center gap-4">
                    <!-- Show Current Only toggle -->
                    <label class="label w-fit gap-1">
                      <span class="label-text text-[11px] text-gray-600">Show Current Only</span>
                      <input
                        type="checkbox"
                        :checked="showCurrentPoliciesOnly"
                        v-model="showCurrentPoliciesOnly"
                        class="toggle border-solid opacity-80 toggle-primary toggle-sm" />
                    </label>

                    <!-- Search bar -->
                    <div class="relative flex-1">
                      <input
                        v-model="policySearchQuery"
                        type="text"
                        placeholder="Search policies by name, number, carrier, or coverage type..."
                        class="input input-sm w-full bg-white pr-8" />
                      <button
                        v-if="policySearchQuery"
                        @click="policySearchQuery = ''"
                        class="absolute top-1/2 right-2 -translate-y-1/2 text-gray-400 transition-colors hover:text-gray-600">
                        <X :size="16" />
                      </button>
                    </div>
                  </div>

                  <!-- All policies list -->
                  <div class="flex flex-col gap-1">
                    <div v-if="allAccountPoliciesLoading" class="h-96 w-full skeleton"></div>
                    <div v-else-if="allAccountPolicies.length === 0" class="py-4 text-center text-gray-500">
                      No policies found for this account
                    </div>
                    <div
                      v-else-if="filteredAccountPolicies.length === 0"
                      class="py-4 text-center text-gray-500">
                      No policies match your search
                    </div>
                    <div v-else class="grid grid-cols-2 gap-2">
                      <div
                        v-for="policy in filteredAccountPolicies"
                        :key="policy.policyID"
                        class="flex cursor-pointer flex-col items-center justify-between rounded-lg border border-solid border-gray-200 p-2 shadow-md"
                        :class="{
                          'border-blue-100 bg-blue-50 shadow-none':
                            Number(policy.uniqPolicy) === selectedOptions.policyId,
                          'hover:bg-gray-50': Number(policy.uniqPolicy) !== selectedOptions.policyId,
                        }"
                        @click="handleSelectPolicy(policy)"
                        data-testid="all-policy">
                        <div class="flex w-full items-start justify-between gap-2">
                          <div
                            class="text-gray-600"
                            v-html="highlightText(policy.description, policySearchQuery)"></div>
                          <div
                            v-if="isExternalPolicy(policy)"
                            class="flex h-6 w-fit shrink-0 items-center rounded-xl px-3 text-[11px] font-semibold"
                            :class="externalChipColor"
                            data-testid="external-policy-chip">
                            {{ externalChipLabel }}
                          </div>
                        </div>
                        <div class="flex w-full flex-col justify-between">
                          <div class="text-[11px] text-gray-600">
                            Carrier: <span v-html="highlightText(policy.carrierGroup, policySearchQuery)"></span>
                          </div>
                          <div class="text-[11px] text-gray-600">
                            Coverage Type: <span v-html="highlightText(policy.coverageType, policySearchQuery)"></span>
                          </div>
                          <div class="text-[11px] text-gray-600">
                            Policy Number: <span v-html="highlightText(policy.policyID, policySearchQuery)"></span>
                          </div>
                          <div
                            v-if="policy.effectiveDate && policy.expirationDate"
                            class="text-[11px] text-gray-600">
                            Effective: {{ new Date(policy.effectiveDate).toLocaleDateString() }} -
                            {{ new Date(policy.expirationDate).toLocaleDateString() }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </TabsRoot>

            <!-- All Policies Tab Content -->
          </div>

          <!-- tags -->
          <div class="p-4" ref="tagsSectionRef">
            <div class="pl-2 text-base text-gray-700">Tags</div>
            <div
              class="relative flex w-full flex-col gap-4 rounded-lg border border-solid border-gray-200 bg-purple-50/30 p-4 shadow-md">
              <div
                v-if="selectedDocuments.length === 0"
                class="absolute h-full w-full cursor-not-allowed rounded-lg bg-gray-100/30 transition-all"></div>
              
              <div class="flex flex-col">
                <label class="label">
                  <span class="label-text text-xs text-gray-500 pb-1">Add tags to documents</span>
                </label>

                <div v-if="selectedTags.length > 0" class="flex flex-wrap pb-2">
                  <div
                    v-for="tag in selectedTags"
                    :key="tag.value"
                    @click.stop="handleTagRemove(tag)"
                    class="group badge flex h-fit cursor-pointer gap-1 badge-ghost badge-xs hover:bg-red-100">
                    <div class="p-1">{{ tag.label }}</div>
                    <X :size="12" class="group-hover:stroke-red-600" />
                  </div>
                </div>

                <Combobox
                  :key="tagsComboboxKey"
                  v-model="selectedTag"
                  input-class="input-sm"
                  :options="availableTagsFiltered"
                  placeholder="Add tags..."
                  :allow-free-text="true"
                  @selected="handleTagSelect" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
